"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import type { UserPlan } from "@/lib/auth/plans";
import {
  resolveEffectivePlan,
  type SubscriptionStatus,
} from "@/lib/auth/subscription";
import { getSupabaseBrowserClient } from "@/lib/api/supabase";
import { captureSignIn, captureSignUp } from "@/lib/analytics/posthog";
import { isFirstSession } from "@/lib/analytics/authSignals";

type UserContextValue = {
  user: User | null;
  plan: UserPlan;
  /**
   * СИРА дата закінчення підписки (НЕ nullиться після прострочення — живить
   * "Pro just ended" notice). Доступ визначає `plan`, не ця дата.
   */
  planExpiresAt: string | null;
  /** Статус підписки Creem з останнього вебхука; null = невідомо (legacy). */
  subscriptionStatus: SubscriptionStatus | null;
  /** plan='pro' у БД, але прострочився повз grace → ефективно free. */
  isLapsedPro: boolean;
  loading: boolean;
  /** Email available immediately from SSR (before onAuthStateChange fires). */
  serverEmail: string | null;
  refresh: () => Promise<void>;
};

const UserContext = createContext<UserContextValue>({
  user: null,
  plan: "anonymous",
  planExpiresAt: null,
  subscriptionStatus: null,
  isLapsedPro: false,
  loading: true,
  serverEmail: null,
  refresh: async () => {},
});

export type ServerAuthHint = {
  email: string | null;
  plan: UserPlan;
};

type UserProviderProps = {
  children: ReactNode;
  serverHint?: ServerAuthHint | null;
};

/**
 * Resolves the browser Supabase client, or null during SSR so the initial
 * server render stays fully deterministic (no non-serialisable singleton is
 * created), which is what lets Next.js prerender the static shell without a
 * <Suspense> boundary around the layout.
 *
 * NOT a hook and NOT ref-cached: `getSupabaseBrowserClient()` is already a
 * module-level singleton, so the old `useRef` added no caching — it only read
 * `ref.current` during render, which React 19 flags ("Cannot access refs
 * during render"). Every caller below invokes this inside an effect or a
 * callback, where touching browser globals is legal.
 */
function browserSupabase() {
  return typeof window === "undefined" ? null : getSupabaseBrowserClient();
}

export function UserProvider({ children, serverHint }: UserProviderProps) {

  const hasHint = serverHint && serverHint.email !== null;
  const [user, setUser] = useState<User | null>(null);
  const [plan, setPlan] = useState<UserPlan>(
    serverHint?.plan ?? "anonymous",
  );
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [isLapsedPro, setIsLapsedPro] = useState(false);
  const [loading, setLoading] = useState(!hasHint);

  const fetchPlan = useCallback(
    async (u: User | null) => {
      const supabase = browserSupabase();
      if (!supabase) return;

      if (!u) {
        setPlan("anonymous");
        setPlanExpiresAt(null);
        setSubscriptionStatus(null);
        setIsLapsedPro(false);
        return;
      }

      // maybeSingle: одразу після signup рядок може ще не існувати
      // (handle_new_user race) — .single() дав би 406.
      const { data } = await supabase
        .from("user_profiles")
        .select("plan, plan_expires_at, subscription_status")
        .eq("id", u.id)
        .maybeSingle();

      // Та сама expiry+grace деривація, що й на сервері (getUserPlan).
      const state = resolveEffectivePlan(data);
      setPlan(state.plan);
      setPlanExpiresAt(state.planExpiresAt);
      setSubscriptionStatus(state.subscriptionStatus);
      setIsLapsedPro(state.isLapsedPro);
    },
    [],
  );

  const refresh = useCallback(async () => {
    const supabase = browserSupabase();
    if (!supabase) return;

    // getClaims() — локальна JWKS-верифікація, без HTTP-хіту в /auth/v1/user.
    // getSession() в @supabase/ssr v0.9 ходить у /auth/v1/user для верифікації
    // JWT, що генерувало тисячі зайвих запитів.
    const { data } = await supabase.auth.getClaims();
    const claims = data?.claims;
    const u = claims?.sub
      ? ({
          id: claims.sub,
          email: typeof claims.email === "string" ? claims.email : "",
        } as User)
      : null;
    setUser(u);
    await fetchPlan(u);
  }, [fetchPlan]);

  useEffect(() => {
    const supabase = browserSupabase();
    if (!supabase) return;

    let mounted = true;

    // Subscribe first so we don't miss events fired by getSession().
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        const u = session?.user ?? null;
        // SIGNED_IN is NOT only a real sign-in — it fires again on every full
        // page load while a session exists, so `captureSignIn` de-duplicates
        // per tab (see its comment). SIGNED_OUT is a genuine one-shot, and
        // `resetUser` in AnalyticsProvider clears the de-dup mark.
        if (_event === "SIGNED_IN" && u?.id) {
          const method = u.app_metadata?.provider ?? "unknown";
          captureSignIn(u.id, method);
          // Only `session.user` carries the auth timestamps this reads — the
          // getClaims() user below is a bare {id, email}.
          if (isFirstSession(u)) captureSignUp(u.id, method);
        }
        setUser(u);
        setLoading(false);
        // Fetch plan in background — never block loading state
        fetchPlan(u).catch((err) => {
          console.warn("[useUser] fetchPlan failed:", err);
        });
      },
    );

    // Fallback: locally-verified claims (no /auth/v1/user network call).
    // onAuthStateChange has already been subscribed above, so INITIAL_SESSION
    // will deliver the full session object asynchronously. We just unblock the
    // loading state once we know whether there's a valid session at all.
    supabase.auth
      .getClaims()
      .then(({ data }) => {
        if (!mounted) return;
        const claims = data?.claims;
        if (claims?.sub) {
          // Pre-set minimal user so consumers see auth state immediately;
          // onAuthStateChange will later overwrite with the full User object.
          setUser((prev) =>
            prev ?? ({
              id: claims.sub,
              email: typeof claims.email === "string" ? claims.email : "",
            } as User),
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        console.warn("[useUser] getClaims failed:", err);
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchPlan]);

  const serverEmail = serverHint?.email ?? null;

  const value = useMemo(
    () => ({
      user,
      plan,
      planExpiresAt,
      subscriptionStatus,
      isLapsedPro,
      loading,
      serverEmail,
      refresh,
    }),
    [
      user,
      plan,
      planExpiresAt,
      subscriptionStatus,
      isLapsedPro,
      loading,
      serverEmail,
      refresh,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  return useContext(UserContext);
}
